import React, { useState } from "react";
import type { Record } from "react-admin";
import {
	List,
	Datagrid,
	TextField,
	FunctionField,
	useNotify,
	Button,
	useRefresh,
	Link,
} from "react-admin";
import {
	Box,
	Typography,
	Tooltip,
	makeStyles,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import BlockIcon from "@material-ui/icons/Block";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useMutation } from "@apollo/client/react";
import LoadMorePagination from "~/components/load-more-pagination.tsx";
import { formatInventoryItemFullValue } from "../inventory-shared/format-inventory-item-value.ts";
import InventoryHistoryActions from "../inventory-shared/inventory-history-actions.tsx";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import xeroErrorsQuery from "~/api/get-xero-errors.query.graphql";
import retryXeroSyncMutation from "~/api/update-xero-sync-retry.mutation.graphql";
import ignoreXeroSyncMutation from "~/api/update-xero-sync-ignore.mutation.graphql";
import TimezoneDateField from "~/components/date-field.tsx";

const useStyles = makeStyles((_theme) => ({
	retryButton: {
		backgroundColor: "#4caf50",
		color: "white",
		marginRight: "8px",
		padding: "0 12px 0 6px",
		"&:hover": {
			backgroundColor: "#388e3c",
		},
	},
	ignoreButton: {
		backgroundColor: "#ff9800",
		color: "white",
		marginRight: "8px",
		padding: "0 12px",
		"&:hover": {
			backgroundColor: "#f57c00",
		},
	},
	viewButton: {
		marginRight: "8px",
		padding: "0 12px",
	},
	dialog: {
		"& .MuiDialog-paper": {
			minWidth: "300px",
		},
	},
}));

function RetryButton(props: any) {
	const { record } = props;
	const notify = useNotify();
	const refresh = useRefresh();
	const [retryXeroSync, { loading }] = useMutation(retryXeroSyncMutation);
	const classes = useStyles();
	const [open, setOpen] = useState(false);

	const handleClick = async () => {
		try {
			await retryXeroSync({
				variables: { id: record.id },
			});
			notify("Xero sync retry initiated", { type: "success" });
			refresh();
		} catch (error) {
			notify("Failed to retry Xero sync", { type: "error" });
			console.error(error);
		}
	};

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<>
			<Button
				label="Retry"
				onClick={handleOpen}
				disabled={loading}
				icon={<RefreshIcon />}
				size="small"
				className={classes.retryButton}
			/>
			<Dialog open={open} onClose={handleClose} className={classes.dialog}>
				<DialogTitle>Confirm Retry</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to retry the Xero sync for this item?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button label="Cancel" onClick={handleClose} size="small" />
					<Button
						label="Retry"
						onClick={async () => {
							handleClose();
							await handleClick();
						}}
						size="small"
						className={classes.retryButton}
					/>
				</DialogActions>
			</Dialog>
		</>
	);
}

function IgnoreButton(props: any) {
	const { record } = props;
	const notify = useNotify();
	const refresh = useRefresh();
	const [ignoreXeroSync, { loading }] = useMutation(ignoreXeroSyncMutation);
	const classes = useStyles();
	const [open, setOpen] = useState(false);

	const handleClick = async () => {
		try {
			await ignoreXeroSync({
				variables: { id: record.id },
			});
			notify("Xero sync issue ignored", { type: "success" });
			refresh();
		} catch (error) {
			notify("Failed to ignore Xero sync issue", { type: "error" });
			console.error(error);
		}
	};

	const handleOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	return (
		<>
			<Button
				label="Ignore"
				onClick={handleOpen}
				disabled={loading}
				icon={<BlockIcon />}
				size="small"
				className={classes.ignoreButton}
			/>
			<Dialog open={open} onClose={handleClose} className={classes.dialog}>
				<DialogTitle>Confirm Ignore</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to ignore this Xero sync issue? This action
						cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button label="Cancel" onClick={handleClose} size="small" />
					<Button
						label="Ignore"
						onClick={async () => {
							handleClose();
							await handleClick();
						}}
						size="small"
						className={classes.ignoreButton}
					/>
				</DialogActions>
			</Dialog>
		</>
	);
}

function XeroErrors(props: any) {
	const classes = useStyles();
	return (
		<List
			{...props}
			title="Xero Sync Errors"
			bulkActionButtons={false}
			sort={{ field: "createdAt", order: "DESC" }}
			pagination={<LoadMorePagination />}
			actions={
				<InventoryHistoryActions
					query={xeroErrorsQuery}
					getData={(data) => data.inventoryItemsWithXeroIssues.data}
					baseFilename="xero-sync-errors"
				/>
			}
		>
			<Datagrid>
				<TimezoneDateField
					source="id"
					sortable={false}
					label="Timestamp"
					showTime
				/>
				<TimezoneDateField source="createdAt" sortable={false} showTime />
				<TextField source="reason" sortable={false} />
				<FunctionField
					source="summary"
					sortable={false}
					render={(record?: Record) => {
						if (!record || !record.summary) {
							return <div />;
						}
						return (
							<div>
								{record.summary.largestItems
									.map(
										(item: any) =>
											`${item.label} - ${formatInventoryItemFullValue(
												record,
												item.value,
											)}`,
									)
									.join(", ")}
								{record.summary.totalItems >
									record.summary.largestItems.length && "..."}
							</div>
						);
					}}
				/>
				<FunctionField
					source="xeroSyncing"
					sortable={false}
					label="Xero Sync"
					render={(record?: Record) => {
						if (record?.type === "cost-price") {
							return (
								<Box display="flex" alignItems="center">
									<Tooltip title="Not Applicable">
										<InfoIcon style={{ color: "#9e9e9e", marginRight: 8 }} />
									</Tooltip>
								</Box>
							);
						}

						if (record?.xeroSyncing?.status === "success") {
							return (
								<Box display="flex" alignItems="center">
									<CheckCircleIcon
										style={{ color: "#4caf50", marginRight: 8 }}
									/>
								</Box>
							);
						}
						if (record?.xeroSyncing?.status === "error") {
							return (
								<Box display="flex" alignItems="center">
									<ErrorIcon color="error" style={{ marginRight: 8 }} />
								</Box>
							);
						}
						if (record?.xeroSyncing?.status === "ignored") {
							return (
								<Box display="flex" alignItems="center">
									<Tooltip title="Ignored">
										<InfoIcon style={{ color: "#ff9800", marginRight: 8 }} />
									</Tooltip>
								</Box>
							);
						}
						return null;
					}}
				/>
				<RetryButton />
				<IgnoreButton />
				<FunctionField
					render={(record?: Record) =>
						record ? (
							<Button
								component={Link}
								to={`/inventoryItems/${record.id}/show`}
								label="Show"
								size="small"
								className={classes.viewButton}
							>
								<VisibilityIcon />
							</Button>
						) : null
					}
				/>
			</Datagrid>
		</List>
	);
}

export default XeroErrors;
